import axios, { AxiosInstance } from 'axios';

// API base URL from environment variable - no fallback to force proper configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  const errorMsg = 'CRITICAL: VITE_API_URL environment variable is not set. Application cannot function.';
  console.error(errorMsg);
  // Don't throw during module initialization - let the app load but fail gracefully when making requests
}

// Debug log to check environment variable
console.log('API Configuration:');
console.log('- VITE_API_URL from env:', import.meta.env.VITE_API_URL);
console.log('- Using API_BASE_URL:', API_BASE_URL);


// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL || '', // Use empty string as fallback to prevent undefined errors
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: import.meta.env.VITE_CORS_CREDENTIALS === 'true', // For cookie-based auth if enabled
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Warn if API URL is not configured but don't block the request
    // Let it fail naturally with network error
    if (!API_BASE_URL) {
      console.error('WARNING: VITE_API_URL is not configured. API requests will fail.');
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with AA backend integration
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - AA backend specific logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Only try to refresh token if we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      const currentToken = localStorage.getItem('auth_token');
      
      // If no refresh token or we're already on auth endpoints, don't attempt refresh
      if (!refreshToken || 
          originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/signup') || 
          originalRequest.url?.includes('/auth/refresh')) {
        
        // Clear tokens silently for expected 401s (like initial auth check)
        if (currentToken) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('tenant_id');
        }
        
        // Only redirect if we have a token (meaning user was previously authenticated)
        // and we're not already on login/auth pages
        if (currentToken && 
            !window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup') &&
            !window.location.pathname.includes('/forgot-password')) {
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }

      // Try to refresh token using AA backend refresh endpoint
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          headers: {
            'Authorization': `Bearer ${refreshToken}`
          }
        });
        
        // Handle AA API response format
        if (response.data.success) {
          const authData = response.data.data;
          const newToken = authData.access_token;
          const newRefreshToken = authData.refresh_token || refreshToken;
          
          localStorage.setItem('auth_token', newToken);
          localStorage.setItem('refresh_token', newRefreshToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          throw new Error(response.data.message || 'Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('tenant_id');
        
        // Only redirect if we're not already on login page
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup') &&
            !window.location.pathname.includes('/forgot-password')) {
          window.location.href = '/login';
        }
        
        // Don't log refresh errors as they're expected in some cases
        console.debug('Token refresh failed:', refreshError);
      }
    }

    // Don't enhance 401 errors for auth endpoints to avoid console spam
    if (error.response?.status === 401 && (
        originalRequest.url?.includes('/auth/') ||
        originalRequest.url?.includes('/users')
    )) {
      return Promise.reject(error);
    }

    // Format error message for AA API responses
    const message = error.response?.data?.message || 
                   error.response?.data?.error || 
                   error.message || 
                   'An error occurred';
    const enhancedError = new Error(message);
    enhancedError.name = 'APIError';
    
    return Promise.reject(enhancedError);
  }
);

// API endpoints configuration - Updated for AA API structure
export const API_ENDPOINTS = {
  // Authentication - AA API endpoints (Passwordless - Magic Link only)
  auth: {
    signup: '/api/users', // AA uses /users for user creation/signup
    logout: '/api/auth/logout',
    sendMagicLink: '/api/auth/send-magic-link', // Magic link endpoint for passwordless auth
    getCurrentUser: '/api/auth/me',
    refreshToken: '/api/auth/refresh',
    verifyEmail: '/api/auth/verify-email',
    resendVerification: '/api/auth/resend-verification',
    googleAuth: '/api/auth/google', // Google OAuth endpoint
  },

  // Health check (root level, no /api/ prefix)
  health: '/health',

  // TallyConnector - Updated for AA backend TC endpoints
  tc: {
    generateExe: '/api/tcg/generate',
    tenantId: '/api/user/tenant_id',
    register: '/api/tc/register', // TC registration endpoint
  },

  // Connection - Updated for AA backend connection endpoints
  connection: {
    status: '/api/connection/status',
    deploy: '/api/connection/deploy',
    fetchCompanies: '/api/connection/fetch_companies',
    disconnect: '/api/connection/disconnect',
    test: '/api/connection/test',
    devices: '/api/connection/devices',
    logs: '/api/connection/logs',
  },

  // Chat - Updated for AA backend endpoints
  // NOTE: All endpoints use /api/chat/... (consistent routing)
  chat: {
    message: '/api/chat/message',
    session: '/api/chat/session',
    sessions: '/api/chat/sessions',
    tools: '/api/chat/tools',
    upload: '/api/chat/upload',
    ws: '/api/chat/ws',  // WebSocket endpoint (consistent with HTTP routes)
    companies: '/api/chat/companies', // Get companies for chat context
  },

  // MCP - Model Context Protocol endpoints
  mcp: {
    status: '/api/mcp/status', // Get real MCP connection status from database
    tools: '/api/mcp/tools',
  },

  // Dashboard endpoints (NOT IMPLEMENTED in backend - will return 404)
  dashboard: {
    stats: '/api/dashboard/stats',
    activity: '/api/dashboard/activity',
    quickActions: '/api/dashboard/quick-actions',
    metrics: '/api/dashboard/metrics',
    export: '/api/dashboard/export',
  },

  // Settings endpoints
  settings: {
    profile: '/api/settings/profile',
    password: '/api/settings/password',
    preferences: '/api/settings/preferences',
    notifications: '/api/settings/notifications',
    devices: '/api/settings/devices',
    sessions: '/api/settings/sessions',
    security: '/api/settings/security',
    integrations: '/api/settings/integrations',
    billing: '/api/settings/billing',
    apiKeys: '/api/settings/api-keys',
    team: '/api/settings/team',
    audit: '/api/settings/audit',
    export: '/api/settings/export',
    deleteAccount: '/api/settings/delete-account',
  },
};

// Export aliases for backward compatibility
export const api = apiClient;
export const tokenManager = {
  getToken: () => localStorage.getItem('auth_token'),
  setToken: (token: string) => localStorage.setItem('auth_token', token),
  removeToken: () => localStorage.removeItem('auth_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setRefreshToken: (token: string) => localStorage.setItem('refresh_token', token),
  removeRefreshToken: () => localStorage.removeItem('refresh_token'),
};

export const setupCSRFProtection = () => {
  // CSRF protection setup if needed
  console.log('CSRF protection setup');
};

export const authEndpoints = API_ENDPOINTS.auth;